import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Check, Plug, Wallet, Store, Smartphone, Webhook, Users, Activity,
  ShieldCheck, Lock, KeyRound, RefreshCw, Menu, X, Hand, Link2, Puzzle,
} from 'lucide-react';
import { Logo, LogoMark } from '../../components/Logo.jsx';
import { useAuth } from '../../lib/auth.jsx';
import {
  CONTACT_EMAIL, EASE, fadeUp, heroIn, Section, SectionHead, Panel,
  PrimaryLink, GhostLink, Float,
} from './atoms.jsx';
import { CheckoutMock, ActivityMock, TillMock } from './Mockups.jsx';

/**
 * The public page at `/`.
 *
 * Rendered outside the app shell — no sidebar, no top bar — and readable
 * signed in or out. It sells one idea: you integrate once, and after that
 * which gateway takes the money is a setting rather than a rewrite.
 *
 * Everything here is drawn from the tokens the product itself uses, so the
 * marketing page and the first screen after sign-up do not look like two
 * different companies. No screenshots, no stock photography, no external
 * assets beyond the wordmark.
 */

const NAV = [
  { id: 'how', label: 'How it works' },
  { id: 'features', label: 'Features' },
  { id: 'methods', label: 'Payment methods' },
  { id: 'security', label: 'Security' },
  { id: 'faq', label: 'FAQ' },
];

const scrollToId = (event, id) => {
  event.preventDefault();
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

/* -------------------------------------------------------------------------- */

function Navbar() {
  const { isAuthenticated, isPlatformAdmin } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // A menu that stays open behind a navigation is a menu covering the page
  // somebody just asked for.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const home = isPlatformAdmin ? '/admin' : '/dashboard';

  return (
    <header
      className="sticky top-0 z-40 border-b transition-colors"
      style={{
        borderColor: scrolled ? 'rgb(var(--line))' : 'transparent',
        background: scrolled ? 'rgb(var(--surface) / 0.85)' : 'transparent',
        backdropFilter: scrolled ? 'saturate(180%) blur(12px)' : undefined,
      }}
    >
      <div className="mx-auto flex h-16 w-full max-w-6xl items-center gap-6 px-4 sm:px-6">
        <Link to="/" aria-label="DistrxPay home"><Logo size={26} /></Link>

        <nav className="ml-2 hidden items-center gap-6 md:flex">
          {NAV.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              onClick={(e) => scrollToId(e, item.id)}
              className="text-[13.5px] text-muted transition-colors hover:text-ink"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="ml-auto hidden items-center gap-2 md:flex">
          {isAuthenticated ? (
            <PrimaryLink to={home} className="btn-sm">
              Go to your dashboard <ArrowRight className="h-3.5 w-3.5" />
            </PrimaryLink>
          ) : (
            <>
              <GhostLink to="/login" className="btn-sm">Sign in</GhostLink>
              <PrimaryLink to="/register" className="btn-sm">
                Create an account <ArrowRight className="h-3.5 w-3.5" />
              </PrimaryLink>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="btn-icon ml-auto md:hidden"
          aria-expanded={open}
          aria-label={open ? 'Close menu' : 'Open menu'}
        >
          {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
        </button>
      </div>

      {open && (
        <div className="border-t md:hidden" style={{ borderColor: 'rgb(var(--line))', background: 'rgb(var(--surface))' }}>
          <div className="mx-auto flex w-full max-w-6xl flex-col gap-1 px-4 py-3">
            {NAV.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={(e) => { scrollToId(e, item.id); setOpen(false); }}
                className="rounded-lg px-2 py-2.5 text-sm text-ink-soft hover:bg-surface-2"
              >
                {item.label}
              </a>
            ))}
            <div className="mt-2 flex flex-col gap-2 border-t pt-3" style={{ borderColor: 'rgb(var(--line-soft))' }}>
              {isAuthenticated ? (
                <PrimaryLink to={home}>Go to your dashboard</PrimaryLink>
              ) : (
                <>
                  <GhostLink to="/login">Sign in</GhostLink>
                  <PrimaryLink to="/register">Create an account</PrimaryLink>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

/* -------------------------------------------------------------------------- */

function Hero() {
  const { isAuthenticated } = useAuth();

  return (
    <section className="relative overflow-hidden px-4 pb-20 pt-14 sm:px-6 sm:pb-28 sm:pt-20">
      {/* A very quiet paper wash. Borders do the work on this page, not glow. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-[520px]"
        style={{ background: 'radial-gradient(60% 60% at 50% 0%, rgb(var(--accent-soft) / 0.5), transparent 70%)' }}
      />

      <div className="relative mx-auto grid w-full max-w-6xl items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
        <div>
          <motion.div {...heroIn(0)}>
            <span
              className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[12px] text-ink-soft"
              style={{ borderColor: 'rgb(var(--line))', background: 'rgb(var(--surface))' }}
            >
              <Plug className="h-3.5 w-3.5 text-accent-ink" aria-hidden="true" />
              One integration. Every gateway.
            </span>
          </motion.div>

          <motion.h1
            {...heroIn(0.06)}
            className="mt-5 text-[38px] font-semibold leading-[1.06] tracking-[-0.02em] text-ink sm:text-[54px]"
          >
            Take payments
            <br />
            without marrying
            <br />
            a payment gateway.
          </motion.h1>

          <motion.p {...heroIn(0.12)} className="mt-5 max-w-xl text-[16px] leading-relaxed text-ink-soft">
            DistrxPay is payment orchestration for your business. Connect Authorize.Net,
            Razorpay, Square, PayPal or CCAvenue — or take the money into your own bank
            account and mark it received. Your customers see your brand either way, and
            switching provider is a setting, not a rewrite.
          </motion.p>

          <motion.div {...heroIn(0.18)} className="mt-8 flex flex-wrap items-center gap-3">
            {isAuthenticated ? (
              <PrimaryLink to="/dashboard">
                Go to your dashboard <ArrowRight className="h-4 w-4" />
              </PrimaryLink>
            ) : (
              <>
                <PrimaryLink to="/register">
                  Create an account <ArrowRight className="h-4 w-4" />
                </PrimaryLink>
                <GhostLink href="#how" onClick={(e) => scrollToId(e, 'how')}>See how it works</GhostLink>
              </>
            )}
          </motion.div>

          <motion.ul {...heroIn(0.24)} className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
            {['No card data on our servers', 'Webhooks that retry', 'Works at a counter too'].map((line) => (
              <li key={line} className="flex items-center gap-2 text-[13px] text-muted">
                <Check className="h-3.5 w-3.5 text-accent-ink" aria-hidden="true" />
                {line}
              </li>
            ))}
          </motion.ul>
        </div>

        <motion.div {...heroIn(0.16)} className="relative mx-auto w-full max-w-md lg:max-w-none">
          <Float duration={9} dy={6}><CheckoutMock /></Float>
        </motion.div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

const PROVIDERS = ['Authorize.Net', 'Razorpay', 'Square', 'PayPal', 'CCAvenue'];

function Providers() {
  return (
    <div className="border-y" style={{ borderColor: 'rgb(var(--line))', background: 'rgb(var(--surface-2))' }}>
      <div className="mx-auto flex w-full max-w-6xl flex-col items-center gap-5 px-4 py-8 sm:px-6 md:flex-row md:gap-10">
        <span className="shrink-0 text-[12px] uppercase tracking-[0.14em] text-faint">Connect</span>
        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 md:justify-start">
          {PROVIDERS.map((name) => (
            <span key={name} className="text-[15px] font-medium text-ink-soft">{name}</span>
          ))}
          <span className="text-[13px] text-faint">…or your own bank account</span>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

const STEPS = [
  {
    n: '01',
    title: 'Create a payment',
    body: 'One POST from your server with an amount, a currency and your order id. You get back a checkout link.',
    code: `curl https://distrxpay.com/api/v1/payments \\
  -H "Authorization: Bearer sk_live_…" \\
  -d amount=184000 \\
  -d currency=INR \\
  -d orderId=ORD-1042`,
  },
  {
    n: '02',
    title: 'Send the customer to it',
    body: 'Redirect, or drop it into your page. They see your name and your colours — never the name of whoever processes the money.',
  },
  {
    n: '03',
    title: 'We tell you what happened',
    body: 'A signed webhook, retried with backoff until your server answers. The status we send is the one we confirmed with the provider, not the one the browser claimed.',
  },
];

function HowItWorks() {
  return (
    <Section id="how">
      <SectionHead
        kicker="How it works"
        title="Three steps, and the third one is the point"
        blurb="Most of what goes wrong with payments happens after the customer leaves the page. That is the part we take."
      />

      <div className="mt-12 grid gap-4 md:grid-cols-3">
        {STEPS.map((step, i) => (
          <motion.div key={step.n} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}>
            <Panel className="h-full p-6" hover>
              <div className="tabular text-[12px] font-medium text-faint">{step.n}</div>
              <h3 className="mt-3 text-[17px] font-semibold tracking-tight text-ink">{step.title}</h3>
              <p className="mt-2 text-[14px] leading-relaxed text-ink-soft">{step.body}</p>
              {step.code && (
                <pre
                  className="mt-4 overflow-x-auto rounded-xl border px-3.5 py-3 text-[11.5px] leading-relaxed text-ink-soft"
                  style={{ borderColor: 'rgb(var(--line))', background: 'rgb(var(--surface-2))' }}
                >
                  {step.code}
                </pre>
              )}
            </Panel>
          </motion.div>
        ))}
      </div>

      {/* What step three leaves behind, which is the part support actually uses. */}
      <motion.div {...fadeUp} className="mt-4">
        <Panel className="grid items-center gap-8 p-6 sm:p-8 lg:grid-cols-[1fr_0.85fr]">
          <div>
            <h3 className="text-[19px] font-semibold tracking-tight text-ink">
              …and every step of it is written down
            </h3>
            <p className="mt-2.5 max-w-md text-[14px] leading-relaxed text-ink-soft">
              Not just the outcome. What the gateway answered and how long it took, which
              attempt it was, who at your counter took the cash, and which payments are
              still waiting on you. Filterable, and kept for 90 days.
            </p>
          </div>
          <ActivityMock />
        </Panel>
      </motion.div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */

const FEATURES = [
  {
    icon: Plug,
    title: 'Providers are a setting',
    body: 'Connect one gateway or five. Your integration never changes, because your integration is with us.',
  },
  {
    icon: Wallet,
    title: 'Methods and routes',
    body: 'Card, UPI, net banking, wallets, bank transfer, cheque, cash — each taken by a gateway or by you, and switched on where you want it.',
  },
  {
    icon: Store,
    title: 'A till, included',
    body: 'Ring up a sale on a browser or a phone. Cash settles on the spot; a cheque waits until it clears, because it should.',
  },
  {
    icon: Smartphone,
    title: 'iOS and Android',
    body: 'The same till, the same payments, the same customers, in an app your staff can carry.',
  },
  {
    icon: Webhook,
    title: 'Webhooks that do not give up',
    body: 'Signed, retried with backoff and jitter, and replayable. An endpoint that fails fifty times in a row is disabled and you are told.',
  },
  {
    icon: Users,
    title: 'Roles that mean something',
    body: 'Owners, admins, staff and viewers. A cashier can take money and refund it; they cannot read an API key.',
  },
  {
    icon: Activity,
    title: 'One activity trail',
    body: 'Every payment, every gateway response, every retry, every setting change and every sign-in — filterable, for 90 days.',
  },
  {
    icon: Puzzle,
    title: 'WooCommerce and Ecwid',
    body: 'Plugins for both, so a shop that is already running can take DistrxPay without writing anything.',
  },
];

function Features() {
  return (
    <Section id="features" tone="sunken">
      <SectionHead
        kicker="What you get"
        title="Everything a payment needs after it leaves your page"
      />

      <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {FEATURES.map((f, i) => (
          <motion.div key={f.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: (i % 4) * 0.06 }}>
            <Panel className="h-full p-5" hover>
              <span
                className="grid h-9 w-9 place-items-center rounded-xl border"
                style={{ borderColor: 'rgb(var(--line))', background: 'rgb(var(--surface-2))' }}
              >
                <f.icon className="h-4 w-4 text-ink-soft" aria-hidden="true" />
              </span>
              <h3 className="mt-3.5 text-[15px] font-semibold tracking-tight text-ink">{f.title}</h3>
              <p className="mt-1.5 text-[13.5px] leading-relaxed text-ink-soft">{f.body}</p>
            </Panel>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */

/** The differentiator, explained rather than asserted. */
function MethodsAndRoutes() {
  return (
    <Section id="methods">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <div>
          <SectionHead
            kicker="Methods and routes"
            title="The same method, two ways round"
            blurb="What the customer taps is one decision. Who actually takes the money is a different one — and most platforms make you pretend they are the same."
          />

          <div className="mt-8 space-y-3">
            <Panel className="flex items-start gap-3.5 p-4">
              <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg border" style={{ borderColor: 'rgb(var(--line))' }}>
                <Link2 className="h-4 w-4 text-ink-soft" aria-hidden="true" />
              </span>
              <span>
                <span className="block text-[14px] font-medium text-ink">Through a gateway</span>
                <span className="mt-0.5 block text-[13px] leading-relaxed text-ink-soft">
                  The provider moves the money and tells us it happened. You do nothing.
                </span>
              </span>
            </Panel>

            <Panel className="flex items-start gap-3.5 p-4">
              <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg border" style={{ borderColor: 'rgb(var(--line))' }}>
                <Hand className="h-4 w-4 text-ink-soft" aria-hidden="true" />
              </span>
              <span>
                <span className="block text-[14px] font-medium text-ink">Into your own account</span>
                <span className="mt-0.5 block text-[13px] leading-relaxed text-ink-soft">
                  Your UPI id, your bank details, your till. No fee, and you mark it received
                  when it arrives — nobody else can, including the customer.
                </span>
              </span>
            </Panel>
          </div>

          <p className="mt-6 max-w-lg text-[13.5px] leading-relaxed text-muted">
            So UPI into your own account costs you nothing and you confirm it, and the same
            UPI through a gateway confirms itself for a fee. Both are legitimate. You choose,
            per method, and per place it appears.
          </p>
        </div>

        <motion.div {...fadeUp} className="space-y-4">
          <TillMock />
          <Panel className="p-4">
            <div className="text-[11px] uppercase tracking-[0.14em] text-faint">At the till, not on a link</div>
            <p className="mt-2 text-[13px] leading-relaxed text-ink-soft">
              Cash is switched on for the counter and off for public links by default —
              otherwise anyone holding the URL could mark an order awaiting payment without
              ever intending to pay.
            </p>
          </Panel>
        </motion.div>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */

const SECURITY = [
  {
    icon: Lock,
    title: 'Card data never reaches us',
    body: 'The card is tokenized in the customer’s browser by the provider’s own script. Our API rejects a request that contains a card number outright.',
  },
  {
    icon: KeyRound,
    title: 'Your gateway credentials are sealed',
    body: 'Encrypted with a versioned envelope, never returned by any endpoint, and never loaded on the platform-admin path at all.',
  },
  {
    icon: RefreshCw,
    title: 'A retry cannot charge twice',
    body: 'Idempotency is a unique index in the database, not a best effort — the second attempt loses to a constraint, not to a race.',
  },
  {
    icon: ShieldCheck,
    title: 'Nobody marks a gateway payment paid',
    body: 'Confirming by hand exists only where you are the one collecting. A button that marks any payment paid is a fraud tool.',
  },
];

function Security() {
  return (
    <section id="security" className="px-4 py-20 sm:px-6 sm:py-24" style={{ background: 'rgb(var(--ink))' }}>
      <div className="mx-auto w-full max-w-6xl">
        <SectionHead
          light
          center
          kicker="Security"
          title="The parts that are hard to undo"
          blurb="Money bugs are not like other bugs: you cannot ship a fix that gives somebody their afternoon back."
        />

        <div className="mt-12 grid gap-4 sm:grid-cols-2">
          {SECURITY.map((s, i) => (
            <motion.div key={s.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: (i % 2) * 0.08 }}>
              <div
                className="h-full rounded-2xl border p-5"
                style={{ borderColor: 'rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)' }}
              >
                <span
                  className="grid h-9 w-9 place-items-center rounded-xl border"
                  style={{ borderColor: 'rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.05)' }}
                >
                  <s.icon className="h-4 w-4" style={{ color: 'rgba(255,255,255,0.8)' }} aria-hidden="true" />
                </span>
                <h3 className="mt-3.5 text-[15px] font-semibold tracking-tight text-white">{s.title}</h3>
                <p className="mt-1.5 text-[13.5px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.66)' }}>
                  {s.body}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */

const FAQ = [
  {
    q: 'Do you touch the money?',
    a: 'No. DistrxPay orchestrates payments; it does not process them. The money moves between your customer and whichever provider you connected, or straight into your own bank account. We are never in the settlement path.',
  },
  {
    q: 'What happens if I switch gateway?',
    a: 'You connect the new one and pick it as the route for the methods it can take. Your server code, your checkout and your webhook handler are untouched, because none of them ever named a provider.',
  },
  {
    q: 'Can my customers tell which gateway you are using?',
    a: 'No, and that is deliberate. The checkout carries your name and your colours, and no provider name or logo is ever shown to a customer.',
  },
  {
    q: 'What if I have no gateway at all?',
    a: 'Then take bank transfers, UPI, cheques or cash into your own account. The checkout works with no provider connected — you confirm each payment when the money arrives.',
  },
  {
    q: 'Is there an API for everything?',
    a: 'Payments, refunds, links, customers and webhooks. The dashboard is a client of the same API you get.',
  },
  {
    q: 'What does it cost?',
    a: 'DistrxPay charges you nothing per transaction — you keep whatever rate you negotiated with your own provider. Get in touch and we will talk about the platform itself.',
  },
];

function Faq() {
  const [open, setOpen] = useState(0);

  return (
    <Section id="faq" tone="sunken">
      <SectionHead kicker="Questions" title="The ones people actually ask" center />

      <div className="mx-auto mt-12 max-w-3xl space-y-2.5">
        {FAQ.map((item, i) => {
          const isOpen = open === i;
          return (
            <Panel key={item.q}>
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => setOpen(isOpen ? -1 : i)}
                className="flex w-full items-center gap-4 px-5 py-4 text-left"
              >
                <span className="flex-1 text-[14.5px] font-medium text-ink">{item.q}</span>
                <span
                  aria-hidden="true"
                  className="grid h-6 w-6 shrink-0 place-items-center rounded-full border text-ink-soft transition-transform"
                  style={{ borderColor: 'rgb(var(--line))', transform: isOpen ? 'rotate(45deg)' : 'none' }}
                >
                  +
                </span>
              </button>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  transition={{ duration: 0.22, ease: EASE }}
                  className="overflow-hidden"
                >
                  <p className="px-5 pb-4 text-[13.5px] leading-relaxed text-ink-soft">{item.a}</p>
                </motion.div>
              )}
            </Panel>
          );
        })}
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */

function FinalCta() {
  const { isAuthenticated } = useAuth();

  return (
    <Section>
      <motion.div {...fadeUp}>
        <Panel className="overflow-hidden">
          <div className="flex flex-col items-center gap-6 px-6 py-14 text-center sm:px-10">
            <LogoMark size={44} />
            <h2 className="max-w-xl text-[28px] font-semibold leading-tight tracking-tight text-ink sm:text-[34px]">
              Start taking payments this afternoon
            </h2>
            <p className="max-w-lg text-[15px] leading-relaxed text-ink-soft">
              Create an account, connect a provider or your own bank details, and send
              your first payment link. There is a simulator for the part before you are ready.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {isAuthenticated ? (
                <PrimaryLink to="/dashboard">Go to your dashboard <ArrowRight className="h-4 w-4" /></PrimaryLink>
              ) : (
                <>
                  <PrimaryLink to="/register">Create an account <ArrowRight className="h-4 w-4" /></PrimaryLink>
                  <GhostLink href={`mailto:${CONTACT_EMAIL}`}>Talk to us</GhostLink>
                </>
              )}
            </div>
          </div>
        </Panel>
      </motion.div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */

export function MarketingFooter() {
  return (
    <footer className="border-t" style={{ borderColor: 'rgb(var(--line))', background: 'rgb(var(--surface-2))' }}>
      <div className="mx-auto w-full max-w-6xl px-4 py-12 sm:px-6">
        <div className="flex flex-col gap-8 sm:flex-row sm:items-start sm:justify-between">
          <div className="max-w-xs">
            <Logo size={26} />
            <p className="mt-3 text-[13px] leading-relaxed text-muted">
              Payment orchestration for businesses that would rather not rewrite their
              checkout every time they change bank.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-x-12 gap-y-6 sm:grid-cols-3">
            <FooterColumn title="Product">
              <FooterLink href="#features">Features</FooterLink>
              <FooterLink href="#methods">Payment methods</FooterLink>
              <FooterLink href="#security">Security</FooterLink>
            </FooterColumn>
            <FooterColumn title="Get started">
              <FooterTo to="/register">Create an account</FooterTo>
              <FooterTo to="/login">Sign in</FooterTo>
              <FooterLink href={`mailto:${CONTACT_EMAIL}`}>Contact</FooterLink>
            </FooterColumn>
            <FooterColumn title="Legal">
              <FooterTo to="/terms">Terms of Service</FooterTo>
              <FooterTo to="/privacy">Privacy Policy</FooterTo>
            </FooterColumn>
          </div>
        </div>

        <div
          className="mt-10 flex flex-col gap-2 border-t pt-6 text-[12px] text-faint sm:flex-row sm:items-center sm:justify-between"
          style={{ borderColor: 'rgb(var(--line))' }}
        >
          <span>© {new Date().getFullYear()} distrx. All rights reserved.</span>
          <span>DistrxPay orchestrates payments. It does not hold or process funds.</span>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ title, children }) {
  return (
    <div>
      <div className="text-[11px] font-medium uppercase tracking-[0.14em] text-faint">{title}</div>
      <div className="mt-3 flex flex-col gap-2">{children}</div>
    </div>
  );
}

/**
 * An in-page anchor that still works from the legal pages.
 *
 * This footer is shared, and `#features` means nothing on /terms — it would
 * scroll to nothing and leave a dead-looking link. Off the landing page it
 * navigates there first.
 */
function FooterLink({ href, children }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isAnchor = href.startsWith('#');

  const onClick = (e) => {
    if (!isAnchor) return;
    e.preventDefault();
    if (pathname === '/') {
      document.getElementById(href.slice(1))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate(`/${href}`);
    }
  };

  return (
    <a href={isAnchor ? href : href} onClick={onClick} className="text-[13px] text-ink-soft transition-colors hover:text-ink">
      {children}
    </a>
  );
}

const FooterTo = ({ to, children }) => (
  <Link to={to} className="text-[13px] text-ink-soft transition-colors hover:text-ink">{children}</Link>
);

/* -------------------------------------------------------------------------- */

export function LandingPage() {
  const { hash } = useLocation();

  /**
   * Arriving at /#security from another page has to land on the section.
   *
   * React Router does not scroll for a hash — it only changes the URL — so
   * without this a link from the footer of /terms drops you at the top of the
   * landing page with a hash that looks like it did nothing.
   */
  useEffect(() => {
    if (!hash) return undefined;
    const id = hash.slice(1);
    const timer = setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
    return () => clearTimeout(timer);
  }, [hash]);

  return (
    <div className="min-h-screen overflow-x-clip" style={{ background: 'rgb(var(--surface))' }}>
      <style>{`
        .mk-hover { transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease; }
        .mk-hover:hover { transform: translateY(-2px); box-shadow: var(--shadow-md); }
        @media (prefers-reduced-motion: reduce) { .mk-hover:hover { transform: none; } }
      `}</style>

      <Navbar />
      <main>
        <Hero />
        <Providers />
        <HowItWorks />
        <Features />
        <MethodsAndRoutes />
        <Security />
        <Faq />
        <FinalCta />
      </main>
      <MarketingFooter />
    </div>
  );
}

export default LandingPage;
